import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  uploadUniversities,
  getChatNotifications,
} from "../../services/admin.services";
import { MarkSideBarChatNotifications } from "../../services/user.services";
import UniversityTable from "./UniversityTable";
import AdminComments from "../common/AdminComments";
import StaticForm from "../student/student_appication_process/StaticForm";
import toast from "react-hot-toast";

const ShortlistingPage = ({ fetchStudentDetails, setMessages }) => {
  const [file, setFile] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeSection, setActiveSection] = useState("all"); // 'all', 'upload', 'table', 'details', 'comments'
  const location = useLocation();
  const id = location.pathname.split("/").pop();
  const selectedStudent = useSelector((state) => state.app.selectedStudent);
  const isSideBarOpen = useSelector((state) => state.application.isSideBarOpen);

  const commentsRef = useRef(null);
  const observer = useRef(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      readExcelFile(selectedFile);
    }
  };

  function readExcelFile(file) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target.result);

      // Read Excel with all options enabled to preserve formatting
      const workbook = XLSX.read(data, {
        type: "array",
        cellDates: true,
        cellStyles: true,
      });

      const sheetName = workbook.SheetNames[0];
      const sheet = workbook.Sheets[sheetName];

      // Get all rows with headers
      const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

      if (rows.length > 1) {
        // Extract headers and convert to lowercase, trim whitespace
        const headers = rows[0].map((header) => {
          if (!header)
            return `column_${Math.random().toString(36).substr(2, 5)}`;

          const raw = String(header)
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "")
            .replace(/\./g, "");

          if (
            [
              "srno",
              "srnumber",
              "srnumber.",
              "sr.no",
              "sr. no",
              "sr no",
            ].includes(raw)
          ) {
            return "sr.no.";
          }

          if (["loginid", "login_id", "login id", "log-inid"].includes(raw)) {
            return "loginid";
          }

          return String(header).trim().toLowerCase(); // fallback
        });

        // Create objects for each row using the lowercase header names
        const jsonData = rows
          .slice(1)
          .map((row) => {
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = row[index] !== undefined ? row[index] : "";
            });
            return obj;
          })
          .filter((row) => {
            // Remove rows where all values are empty strings
            return Object.values(row).some((value) => value !== "");
          });

        // Store both the headers array and data rows
        setJsonData({
          headers: headers, // This exact array (lowercase) will be sent to the backend
          rows: jsonData,
        });
      } else {
        toast.error(
          "Excel file must contain headers and at least one data row"
        );
        setJsonData({ headers: [], rows: [] });
      }
    };

    reader.readAsArrayBuffer(file);
  }

  const handleCancel = () => {
    setFile(null);
    setJsonData(null);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast.error("Please select a file before submitting.");
      return;
    }

    setLoading(true);

    try {
      if (!jsonData || jsonData.headers.length === 0) {
        throw new Error("No valid headers detected in the Excel file");
      }
      const shortlistingWithOrder = [
        { __columnOrder: jsonData.headers }, // Special first object containing column order
        ...jsonData.rows, // Rest of the data rows
      ];
      // CRITICAL: Ensure exact column order is preserved in data structure
      const data = {
        student_id: id,
        shortlisting: shortlistingWithOrder, // Column order is now embedded
      };

      // Upload to the server
      const response = await uploadUniversities(data);

      if (response) {
        // Force refresh student details to get updated data with correct column order
        await fetchStudentDetails();
        toast.success("Shortlisting uploaded successfully");

        setFile(null);
        setJsonData(null);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error(error.message || "Failed to upload. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Add column order visualization component
  const ColumnOrderPreview = ({ columns }) => {
    if (!columns || columns.length === 0) return null;

    return (
      <div className="mt-2">
        <strong className="text-sm text-gray-700">Column Order:</strong>
        <div className="flex flex-wrap items-center mt-1">
          {columns.map((col, index) => (
            <React.Fragment key={index}>
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs font-mono">
                {col}
              </span>
              {index < columns.length - 1 && (
                <span className="text-gray-400 mx-1">→</span>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!commentsRef.current) return;

    const handleIntersection = (entries) => {
      const entry = entries[0];
      if (entry.isIntersecting) {
        markNotificationsAsRead("shortlisting_of_universities");
      }
    };

    observer.current = new IntersectionObserver(handleIntersection, {
      threshold: 0.3,
    });

    observer.current.observe(commentsRef.current);

    return () => {
      if (observer.current && commentsRef.current) {
        observer.current.unobserve(commentsRef.current);
      }
    };
  }, []);

  const markNotificationsAsRead = async (title) => {
    try {
      const response = await MarkSideBarChatNotifications();
      if (response) {
        const res = await getChatNotifications();
        setMessages(res.data);
      }
    } catch (error) {
      console.error("Error marking as read:", error);
    }
  };

  const downloadSampleExcel = () => {
    // Sample columns in a specific order
    const sampleData = [
      [
        "Sr.No.", // ✅ New column
        "Tag",
        "course",
        "university",
        "country",
        "applyLink",
        "deadline",
        "remarks",
      ],
      [
        1,
        "Safe",
        "Masters in Engineering",
        "MIT",
        "USA",
        "https://apply.mit.edu",
        "15 Dec 2025",
        "Top choice",
      ],
      [
        2,
        "Ambitious",
        "Computer Science",
        "Stanford",
        "USA",
        "https://apply.stanford.edu",
        "10 Jan 2026",
        "Reach school",
      ],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sample");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Sample_Shortlisting_Universities.xlsx");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Render the upload form section
  const renderUploadForm = () => (
    <div className="p-6">
      <div className="mb-4">
        <a
          onClick={downloadSampleExcel}
          className="text-blue-500 underline hover:text-blue-700 cursor-pointer text-sm flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4 mr-1"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
            />
          </svg>
          Download Sample Excel Format
        </a>
      </div>
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50 relative hover:border-purple-500 transition">
        <input
          type="file"
          accept=".xlsx, .xls"
          className="absolute inset-0 opacity-0 cursor-pointer"
          onChange={handleFileChange}
        />
        <div className="flex flex-col items-center justify-center py-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-10 w-10 text-gray-400 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
            />
          </svg>
          <p className="text-gray-500 text-center">
            {file ? file.name : "Click or drag file to upload"}
          </p>
          <p className="text-xs text-gray-400 mt-1">
            Supports Excel files (.xlsx, .xls)
          </p>
        </div>
      </div>

      {jsonData && (
        <div className="mt-4 bg-gray-100 p-4 rounded">
          <h3 className="text-gray-700 font-medium">File Preview:</h3>
          <p className="text-sm text-gray-600">
            {jsonData.headers.length} columns and {jsonData.rows.length} rows
            detected
          </p>

          {/* Enhanced column order visualization */}
          <ColumnOrderPreview columns={jsonData.headers} />

          {/* Show sample data if available */}
          {jsonData.rows.length > 0 && (
            <div className="mt-3 text-xs">
              <p className="text-gray-700 font-medium">First row preview:</p>
              <div className="bg-white p-2 rounded border mt-1 overflow-x-auto">
                <pre className="text-gray-600">
                  {JSON.stringify(jsonData.rows[0], null, 2)}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end items-center mt-6 gap-3">
        <button
          onClick={handleCancel}
          className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50"
          disabled={!file || loading}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 flex items-center"
          disabled={!file || loading}
        >
          {loading ? (
            <>
              <svg
                className="animate-spin h-5 w-5 mr-2 border-t-2 border-white border-solid rounded-full"
                viewBox="0 0 24 24"
              ></svg>
              Uploading...
            </>
          ) : (
            "Upload Universities"
          )}
        </button>
      </div>
    </div>
  );

  // Render empty state for university table
  const renderEmptyUniversityTable = () => (
    <div className="py-12 text-center text-gray-500">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-12 w-12 mx-auto text-gray-400 mb-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
        />
      </svg>
      <p>No universities have been shortlisted yet.</p>
      <p className="mt-2 text-sm">Upload an Excel file to add universities.</p>
    </div>
  );

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <div className="max-w-7xl mx-auto">
        {/* Section Navigation */}
        <div className="bg-white rounded-lg shadow mb-6 p-4">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActiveSection("all")}
              className={`px-4 py-2 rounded-md transition ${
                activeSection === "all"
                  ? "bg-purple-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              All Sections
            </button>
            <button
              onClick={() => setActiveSection("details")}
              className={`px-4 py-2 rounded-md transition ${
                activeSection === "details"
                  ? "bg-purple-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Student Details
            </button>
            <button
              onClick={() => setActiveSection("upload")}
              className={`px-4 py-2 rounded-md transition ${
                activeSection === "upload"
                  ? "bg-purple-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Upload Universities
            </button>
            <button
              onClick={() => setActiveSection("table")}
              className={`px-4 py-2 rounded-md transition ${
                activeSection === "table"
                  ? "bg-purple-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              University Table
            </button>
            <button
              onClick={() => setActiveSection("comments")}
              className={`px-4 py-2 rounded-md transition ${
                activeSection === "comments"
                  ? "bg-purple-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Comments
            </button>
          </div>
        </div>

        {/* DETAILS SECTION - FULL WIDTH WHEN EXPANDED */}
        {activeSection === "details" && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold">
                Student Application Details
              </h2>
              <button
                onClick={() => setActiveSection("all")}
                className="text-xs bg-white text-purple-600 px-2 py-1 rounded"
              >
                Back to All
              </button>
            </div>
            <div className="p-4">
              <StaticForm />
            </div>
          </div>
        )}

        {/* UPLOAD SECTION - FULL WIDTH WHEN EXPANDED */}
        {activeSection === "upload" && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold">
                Upload Shortlisted Universities
              </h2>
              <button
                onClick={() => setActiveSection("all")}
                className="text-xs bg-white text-purple-600 px-2 py-1 rounded"
              >
                Back to All
              </button>
            </div>
            {renderUploadForm()}
          </div>
        )}

        {/* TABLE SECTION - FULL WIDTH WHEN EXPANDED */}
        {activeSection === "table" && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold">
                Shortlisted Universities
              </h2>
              <button
                onClick={() => setActiveSection("all")}
                className="text-xs bg-white text-purple-600 px-2 py-1 rounded"
              >
                Back to All
              </button>
            </div>
            <div className="p-4">
              {selectedStudent && selectedStudent.shortlisting ? (
                <UniversityTable
                  activeTab={"shortlisting"}
                  fetchStudentDetails={fetchStudentDetails}
                />
              ) : (
                renderEmptyUniversityTable()
              )}
            </div>
          </div>
        )}

        {/* COMMENTS SECTION - FULL WIDTH WHEN EXPANDED */}
        {activeSection === "comments" && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center">
              <h2 className="text-lg font-semibold">Comments & Discussion</h2>
              <button
                onClick={() => setActiveSection("all")}
                className="text-xs bg-white text-purple-600 px-2 py-1 rounded"
              >
                Back to All
              </button>
            </div>
            <div ref={commentsRef} className="p-4 min-h-96">
              <AdminComments
                activeTab={{
                  name: "Shortlisting",
                  title: "shortlisting_of_universities",
                  count: 3,
                }}
              />
            </div>
          </div>
        )}

        {/* ALL SECTIONS VIEW - GRID LAYOUT */}
        {activeSection === "all" && (
          <>
            {/* Top row - 2 column grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Student Details Section */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center">
                  <h2 className="text-lg font-semibold">
                    Student Application Details
                  </h2>
                  <button
                    onClick={() => setActiveSection("details")}
                    className="text-xs bg-white text-purple-600 px-2 py-1 rounded"
                  >
                    Expand
                  </button>
                </div>
                <div className="p-4 max-h-96 overflow-auto">
                  <StaticForm />
                </div>
              </div>

              {/* Upload Universities Section */}
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center">
                  <h2 className="text-lg font-semibold">
                    Upload Shortlisted Universities
                  </h2>
                  <button
                    onClick={() => setActiveSection("upload")}
                    className="text-xs bg-white text-purple-600 px-2 py-1 rounded"
                  >
                    Expand
                  </button>
                </div>
                {renderUploadForm()}
              </div>
            </div>

            {/* University Table - Full Width */}
            <div className="bg-white rounded-lg shadow mt-6 overflow-hidden">
              <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center">
                <h2 className="text-lg font-semibold">
                  Shortlisted Universities
                </h2>
                <button
                  onClick={() => setActiveSection("table")}
                  className="text-xs bg-white text-purple-600 px-2 py-1 rounded"
                >
                  Expand
                </button>
              </div>
              <div className="p-4 max-h-1/2 overflow-auto">
                {selectedStudent && selectedStudent.shortlisting ? (
                  <UniversityTable
                    activeTab={"shortlisting"}
                    fetchStudentDetails={fetchStudentDetails}
                  />
                ) : (
                  renderEmptyUniversityTable()
                )}
              </div>
            </div>

            {/* Comments Section - Full Width */}
            <div className="bg-white rounded-lg shadow mt-6 overflow-hidden">
              <div className="bg-purple-600 text-white px-6 py-4 flex justify-between items-center">
                <h2 className="text-lg font-semibold">Comments & Discussion</h2>
                <button
                  onClick={() => setActiveSection("comments")}
                  className="text-xs bg-white text-purple-600 px-2 py-1 rounded"
                >
                  Expand
                </button>
              </div>
              <div ref={commentsRef} className="p-4 h-1/2 overflow-auto">
                <AdminComments
                  activeTab={{
                    name: "Shortlisting",
                    title: "shortlisting_of_universities",
                    count: 3,
                  }}
                />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ShortlistingPage;
